'use client';

/* ═══════════════════════════════════════════════════
   JacquardProvider — Root provider for the Jacquard kit

   Sets `data-theme`, `data-a11y-preset`, and `data-density` on <html>.

   v5 additions (ADR-025):
   - `brand` — seed + engineVersion for algorithmic theming
   - `mode` — light / dark / system theme selection

   SSR integration (ADR-023 §5):
   When `<JacquardScript />` runs before hydration, it sets
   `window.__JACQUARD__` with the resolved theme/dir/locale.
   This provider reads that snapshot to avoid overwriting the
   script's cookie-resolved values.

   Usage:
     <JacquardProvider brand={{ seed: '#3b82f6' }} mode="dark" a11y="aaa">
       <App />
     </JacquardProvider>
   ═══════════════════════════════════════════════════ */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export interface BrandConfig {
  /** Brand seed hex color for algorithmic palette regeneration. */
  seed?: string;
  /** Brand engine version for reproducible regeneration. */
  engineVersion?: string;
}

export interface JacquardProviderProps {
  /** Brand configuration for algorithmic theming (ADR-007). */
  brand?: BrandConfig;
  /** Theme mode. 'system' follows OS preference. */
  mode?: 'light' | 'dark' | 'system';
  /** Accessibility conformance level.
   *  - `'aa'` (default) — WCAG AA floor, no overrides applied.
   *  - `'aaa'` — activates the 8-flip AAA preset (ADR-014 §3). */
  a11y?: 'aa' | 'aaa';
  /** Ambient density (ADR-028). Sets `data-density` on <html> so every
   *  primitive in the tree picks up compact/comfortable padding rules
   *  via descendant selectors in density.css. Per-component overrides
   *  still win when a primitive carries its own `data-density`.
   *
   *  Tri-state semantics (codex #108 R4/R5/R6):
   *  - `undefined` (prop omitted) — the provider does NOT touch
   *    `data-density`. If JacquardScript or the SSR template already set
   *    it from the `jq-density` cookie, that value sticks. `useJacquard()
   *    .density` resolves from `window.__JACQUARD__.density` (or
   *    `'default'` when neither is set).
   *  - `'default'` (explicit) — the provider REMOVES any pre-existing
   *    `data-density` attribute. Adopters use this to override a cookie
   *    back to no ambient density.
   *  - `'compact'` — tighter padding for data-dense surfaces.
   *  - `'comfortable'` — generous padding for marketing/content surfaces.
   *
   *  SSR / FOUC: this prop applies in a `useEffect`, so SSR-rendered
   *  pages paint once without `data-density` before React hydrates unless
   *  the attribute is also set server-side. To avoid a density layout
   *  shift on first paint, EITHER:
   *  - render <JacquardScript /> in <head> AND set a `jq-density=compact|
   *    comfortable` cookie server-side — the script applies the attribute
   *    pre-hydration. Leave this prop undefined; the script's value sticks.
   *  - render the `<html data-density="compact">` attribute directly in
   *    your server-side template (Next.js `_document`, Remix `root`,
   *    etc.) so the markup carries the attribute on first paint. Same as
   *    above — leave this prop undefined. */
  density?: 'default' | 'compact' | 'comfortable';
  children: ReactNode;
}

export interface JacquardContextValue {
  brand: BrandConfig;
  mode: 'light' | 'dark' | 'system';
  resolvedTheme: 'light' | 'dark' | null;
  a11y: 'aa' | 'aaa';
  density: 'default' | 'compact' | 'comfortable';
}

export const JacquardContext = createContext<JacquardContextValue>({
  brand: {},
  mode: 'system',
  resolvedTheme: null,
  a11y: 'aa',
  density: 'default',
});

export function useJacquard(): JacquardContextValue {
  return useContext(JacquardContext);
}

// ── SSR bridge: read window.__JACQUARD__ set by JacquardScript ──

interface JacquardSnapshot {
  theme?: string;
  dir?: string;
  locale?: string;
  /** Cookie-resolved ambient density (`'compact' | 'comfortable'`) or null
   *  when no jq-density cookie was present or the value was invalid.
   *  Mirrors window.__JACQUARD__ written by <JacquardScript />. */
  density?: 'compact' | 'comfortable' | null;
}

declare global {
  interface Window {
    __JACQUARD__?: JacquardSnapshot;
  }
}

function getScriptSnapshot(): JacquardSnapshot | null {
  return typeof window !== 'undefined' ? (window.__JACQUARD__ ?? null) : null;
}

function getServerSnapshot(): JacquardSnapshot | null {
  return null;
}

const NOOP_SUBSCRIBE = () => () => {};

export function JacquardProvider({
  brand = {},
  mode = 'system',
  a11y = 'aa',
  density,
  children,
}: JacquardProviderProps) {
  // Read the snapshot set by <JacquardScript /> before hydration.
  // This is a stable value (set once, never changes), so a no-op subscribe is correct.
  const scriptSnapshot = useSyncExternalStore(NOOP_SUBSCRIBE, getScriptSnapshot, getServerSnapshot);

  /* Server-template fallback for the context's density resolution. When an
     adopter follows the documented "render <html data-density='compact'>
     directly in the SSR template" path (without JacquardScript), there's
     no window.__JACQUARD__ snapshot — but the DOM attribute is the
     pre-paint truth.

     Codex audit #108 R7 → R8: reading the DOM during the FIRST client
     render would produce different output than the server render (which
     has no document), triggering a React hydration mismatch. Defer the
     read to a useEffect so the initial client render still matches the
     server (both return null), then a second render after hydration
     picks up the DOM attribute. */
  const [serverRenderedDensity, setServerRenderedDensity] = useState<
    'compact' | 'comfortable' | null
  >(null);
  useEffect(() => {
    const dom = document.documentElement.getAttribute('data-density');
    if (dom === 'compact' || dom === 'comfortable') {
      setServerRenderedDensity(dom);
    }
  }, []);

  useEffect(() => {
    if (a11y === 'aaa') {
      document.documentElement.setAttribute('data-a11y-preset', 'aaa');
    } else {
      document.documentElement.removeAttribute('data-a11y-preset');
    }
    return () => {
      document.documentElement.removeAttribute('data-a11y-preset');
    };
  }, [a11y]);

  useEffect(() => {
    /* Tri-state density handling — codex audit #108 R4/R5/R6/R7 refined this:
     *   density === 'compact' | 'comfortable'
     *     SET data-density. Cleanup RESTORES to the cookie-set value if
     *     scriptSnapshot has one (R7), else removes — so going from
     *     compact override back to omitted prop returns to the saved
     *     cookie density instead of dropping to default.
     *   density === 'default'
     *     The adopter explicitly wants no ambient density. REMOVE the attr
     *     even if a cookie/server-template had set it. No cleanup needed
     *     (we already cleared it).
     *   density === undefined (prop omitted)
     *     LEAVE THE ATTR ALONE. JacquardScript or the SSR template may have
     *     set it pre-paint from the jq-density cookie; clobbering on mount
     *     causes a layout shift. No cleanup either, so StrictMode's
     *     mount→unmount→remount cycle doesn't strip the preserved attribute. */
    const root = document.documentElement;
    /* When restoring after an override, prefer JacquardScript's snapshot,
       then fall back to the DOM attribute captured at hydration. Codex
       audit #108 R10: a previous version only checked scriptSnapshot
       so the SSR-template path (raw <html data-density='compact'>
       without JacquardScript) was lost on default→undefined transitions. */
    const savedDensity = scriptSnapshot?.density ?? serverRenderedDensity;
    if (density === 'compact' || density === 'comfortable') {
      root.setAttribute('data-density', density);
      return () => {
        if (savedDensity === 'compact' || savedDensity === 'comfortable') {
          root.setAttribute('data-density', savedDensity);
        } else {
          root.removeAttribute('data-density');
        }
      };
    }
    if (density === 'default') {
      root.removeAttribute('data-density');
      return () => {
        /* When density transitions back to undefined (settings flow:
           explicit-clear → omitted → restore saved), restore the saved
           density so the next render's no-op leaves it in place. If next
           state is 'compact'|'comfortable', the next effect overwrites
           this immediately. Codex audit #108 R9/R10. */
        if (savedDensity === 'compact' || savedDensity === 'comfortable') {
          root.setAttribute('data-density', savedDensity);
        }
      };
    }
    return; // density === undefined: no-op + no cleanup
  }, [density, scriptSnapshot, serverRenderedDensity]);

  useEffect(() => {
    if (mode === 'system') {
      /* When mode='system', do NOT touch data-theme.
         JacquardScript already resolved the cookie / OS preference and set it.
         Touching it would undo the script's work and cause a flash.
         The CSS @media (prefers-color-scheme) fallback handles system mode
         via :root:not([data-theme]) in tokens.css.

         Audit Finding #2: explicitly return WITHOUT a cleanup function.
         Previously the cleanup unconditionally removed data-theme even
         when this effect never set it, which stripped the script's
         pre-paint value on StrictMode unmount/remount. */
      return;
    }
    const root = document.documentElement;
    const prev = root.getAttribute('data-theme');
    root.setAttribute('data-theme', mode);
    return () => {
      /* Restore prior value rather than blindly removing — mirrors the
         density-effect ownership model already used above. */
      if (prev !== null) root.setAttribute('data-theme', prev);
      else root.removeAttribute('data-theme');
    };
  }, [mode]);

  const resolvedTheme =
    mode !== 'system' ? mode : ((scriptSnapshot?.theme as 'light' | 'dark') ?? null);

  /* When the adopter passes density explicitly, that wins. When the prop
     is omitted (undefined), fall back to whatever JacquardScript resolved
     from the jq-density cookie. Explicit 'default' is the adopter saying
     "no ambient density" — that beats any cookie value. Codex audit #108
     R5/R6 refined this: the previous shape conflated undefined and
     'default', so adopters couldn't override a cookie-set density back
     to default, and consumers branching on context read the wrong value. */
  const resolvedDensity: 'default' | 'compact' | 'comfortable' =
    density !== undefined
      ? density
      : (scriptSnapshot?.density ?? serverRenderedDensity ?? 'default');

  const value = useMemo(
    () => ({ brand, mode, a11y, density: resolvedDensity, resolvedTheme }),
    [brand, mode, a11y, resolvedDensity, resolvedTheme],
  );

  return <JacquardContext.Provider value={value}>{children}</JacquardContext.Provider>;
}
